import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { AxiosError, CancelTokenSource } from 'axios';
import { FormApi } from 'final-form';
import React, { Component } from 'react';
import { Form as FinalForm, FormRenderProps } from 'react-final-form'
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Redirect, RouteComponentProps } from 'react-router';
import CredentialsSegment from 'routes/login/segments/CredentialsSegment';
import ForgotPasswordSegment from 'routes/login/segments/ForgotPasswordSegment';
import MandatesSegment from 'routes/login/segments/MandatesSegment';
import ResetPasswordSegment from 'routes/login/segments/ResetPasswordSegment';
import ValidatingTokenSegment from 'routes/login/segments/ValidatingTokenSegment';
import { Form, Grid, Header, Message, MessageProps, Segment } from 'semantic-ui-react';
import { requestPasswordReset, resetPassword, validateToken } from 'services/passwordResetServices';
import { saveUserConfig } from 'services/userConfigServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import { RequestResetPasswordDto, SetPasswordDto, UpsertUserConfigDto, UserDto } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { noop } from 'utils/functionUtils';
import { ModuleDef } from 'utils/moduleUtils';

import { IndexSignature } from 'utils/praxTypes';
import { required } from 'utils/validatorUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const StyledSegment = styled(Segment)`
  height: 100vh !important;
`;

const StyledForm = styled(Form)`
  height: 100% !important;
`;

const StyledGrid = styled(Grid)`
  height: 100% !important;
`;

const StyledRow = styled(Grid.Row)`
  padding-top: 0 !important;
  padding-bottom: 0 !important;
`;

const StyledHeader = styled(Header)`
  position: absolute !important;
  top: 50%;
  left: 50%;
  font-size: 32px !important;
  transform: translateX(-50%) translateY(-50%);
`;

const Gradient = styled(Grid.Column)`
  background: linear-gradient(to right bottom, #f9fafb 0%, #a0bbc5 90%);
  height: 100% !important;
  @media only screen and (max-width: 767px) {
    display: none !important;
  }
`;

enum Error {
  UNKNOWN_ACCOUNT_EMAIL = "UNKNOWN_ACCOUNT_EMAIL",
  PASSWORD_RESET_TOKEN_INVALID = "PASSWORD_RESET_TOKEN_INVALID",
  PASSWORD_RESET_TOKEN_EXPIRED = "PASSWORD_RESET_TOKEN_EXPIRED",
  INVALID_EMAIL_ADDRESS = "INVALID_EMAIL_ADDRESS"
}

interface FormValues {
  username: string;
  password: string;
  verificationCode: string;
  repeatPassword: string;
  mandate: number;
  email: string;
}

type Segments = "enterCredentialsVisible" | "chooseMandateVisible" |
    "forgotPasswordVisible" | "validatingTokenVisible" | "resetPasswordVisible";

interface Props extends RouteComponentProps<any>, MessageSourceProps, AuthConsumerRenderProps {
  token?: string
}

interface State extends IndexSignature {
  success: boolean;
  error: boolean;
  errorMessage: string;
  submitting: boolean;
  enterCredentialsVisible: boolean;
  verificationCodeKey: number;
  verificationCodeInput: boolean;
  verificationCodeError: boolean;
  chooseMandateVisible: boolean;
  forgotPasswordVisible: boolean;
  validatingTokenVisible: boolean;
  resetPasswordVisible: boolean;
  cancelTokenSource: CancelTokenSource;
}

class Login extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {history: {location}} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    const isResetPassword = location.pathname.includes("/reset-password");
    if (isResetPassword) {
      this.validateToken(cancelTokenSource);
    }

    this.state = {
      success: false,
      error: false,
      errorMessage: "",
      submitting: false,
      enterCredentialsVisible: !props.currentUser && !isResetPassword,
      verificationCodeKey: 0,
      verificationCodeInput: false,
      verificationCodeError: false,
      chooseMandateVisible: !!props.currentUser && !isResetPassword,
      forgotPasswordVisible: false,
      validatingTokenVisible: isResetPassword,
      resetPasswordVisible: false,
      cancelTokenSource
    }
  }

  validatorImpl = (value: string, allValues: Partial<FormValues>): string | undefined => {

    const {getMessage} = this.props;

    if (!required(value)) {
      if (allValues.password != allValues.repeatPassword) {
        this.setState({
          error: true,
          errorMessage: getMessage('resetPassword.passwordsDontMatch')
        });

        return "Required";
      } else if (allValues.password!.length < 8) {
        this.setState({
          error: true,
          errorMessage: getMessage('resetPassword.passwordFieldLength')
        });

        return "Required";
      }

      this.setState({
        error: false,
        errorMessage: ""
      });
    } else {
      return "Required"
    }
  };

  resetErrors = () => {
    this.setState({
      error: false,
      errorMessage: ""
    })
  };

  startTransitioningTo = (segmentFrom: Segments, segmentTo: Segments, form?: FormApi) => {

    if (form) {
      form.reset();
    }

    this.resetErrors();
    this.setState({
      success: false,
      [segmentFrom]: false
    });

    this.transitionTo(segmentTo);
  };

  transitionTo = debounce((segment: Segments) => {
    this.setState({
      [segment]: true
    })
  }, 500);

  removeVerificationCodeInput = () => {
    this.setState({
      verificationCodeKey: this.state.verificationCodeKey + 1,
      verificationCodeInput: false,
      verificationCodeError: false
    });
  }

  goToLogin = () => {

    const {history} = this.props;

    history.push("/login");
  };

  handleSuccess = () => {
    this.resetErrors();
    this.setState({
      success: true,
      submitting: false
    })
  };

  setErrorMessage = (message: string | undefined) => {
    const {getMessage} = this.props;
    this.setState({
      error: true,
      errorMessage: message ? getMessage(message) : "",
      submitting: false
    })
  };

  handleError = (error: AxiosError, form?: FormApi) => {

    const messageKey = error.response && error.response!.status == 401 ? undefined : "general.unknownError";

    if (error.response) {

      const {violations, error: errorCode} = error.response!.data;

      if (errorCode && errorCode === errorUtils.invalidVerificationCode) {
        this.setState((prevState) => {
          return {
            verificationCodeKey: prevState.verificationCodeKey + 1,
            verificationCodeInput: true,
            verificationCodeError: prevState.verificationCodeInput,
            submitting: false
          }
        });

        if (form) {
          form.change("verificationCode", undefined);
        }
        setTimeout(() => {
          (document.getElementsByClassName("verification-input")[0]! as any).focus();
        }, 50);

        return;
      }

      if (violations) {
        const violation = violations[0];
        switch (violation.errorCode) {
          case Error.INVALID_EMAIL_ADDRESS: {
            this.setErrorMessage("resetPassword.validEmail");
            return;
          }
          case Error.UNKNOWN_ACCOUNT_EMAIL: {
            this.setErrorMessage("resetPassword.unknownEmail");
            return;
          }
          case Error.PASSWORD_RESET_TOKEN_EXPIRED:
          case Error.PASSWORD_RESET_TOKEN_INVALID: {
            this.setErrorMessage("resetPassword.tokenExpired");
            return;
          }
        }
      }
    }
    this.setErrorMessage(messageKey);
  };

  validateToken = (cancelTokenSource: CancelTokenSource) => {

    const {token, getMessage} = this.props;

    const transitionToResetPassword = () =>
        this.startTransitioningTo("validatingTokenVisible", "resetPasswordVisible");

    if (token) {
      validateToken(token, cancelTokenSource)
          .then(transitionToResetPassword)
          .catch(this.handleError);
    } else {
      this.setState({
        error: true,
        errorMessage: getMessage("resetPassword.tokenExpired")
      })
    }
  };

  login = (values: Partial<FormValues>, form: FormApi) => {

    const {notificationSystem, getMessage, getMessageWithNamedParams, updateUserConfig} = this.props;
    const {login, history, location} = this.props;
    const {cancelTokenSource} = this.state;

    if (login) {
      login(values.username!, values.password!, values.verificationCode)
          .then((user: UserDto) => {

            const {userConfig: {mandateId}, accountMandates} = user;
            const userModulePath = this.getFirstUsersModule();
            const {from} = location.state || {from: {pathname: userModulePath}};


            if (mandateId || accountMandates.length == 1) {
              const mandate = mandateId ?
                  accountMandates.find(mandate => mandate.id == mandateId) : accountMandates[0];
              const messageKey = mandateId ? "login.lastMandate" : "login.loggedMandate";

              // @ts-ignore
              const notification = notificationSystem.current;
              if (notification) {
                notification.addNotification({
                  title: getMessage("login.loggedIn"),
                  message: `${getMessageWithNamedParams(messageKey, {mandate: mandate!.name})}`,
                  level: 'info'
                });
              }

              if (!mandateId) {
                const upsertUserConfig: Partial<UpsertUserConfigDto> = {
                  mandateId: accountMandates[0].id
                };

                saveUserConfig(upsertUserConfig, cancelTokenSource).then(response => {
                  updateUserConfig(response);
                  history.push(from.pathname);
                }).catch(noop);
              } else {
                history.push(from.pathname);
              }
            } else {
              this.startTransitioningTo("enterCredentialsVisible", "chooseMandateVisible");
            }
          })
          .catch(error => {
            this.handleError(error, form);
          });
    }
  };

  getFirstUsersModule = () => {
    const {initialModule} = this.props;
    const module: ModuleDef | undefined = initialModule();
    return module ? module.path : "/";
  }

  resetPassword = ({password}: Partial<FormValues>) => {

    const {cancelTokenSource} = this.state;

    const setPasswordDto: SetPasswordDto = {
      password: password!
    };

    const {token} = this.props;
    resetPassword(token!, setPasswordDto, cancelTokenSource)
        .then(this.handleSuccess)
        .catch(this.handleError)
  };

  createResetPasswordToken = (email: string) => {

    const {cancelTokenSource} = this.state;

    const requestPasswordResetDto: RequestResetPasswordDto = {
      email: email
    };

    requestPasswordReset(requestPasswordResetDto, cancelTokenSource)
        .then(this.handleSuccess)
        .catch(this.handleError);
  };

  handleSubmit = (values: Partial<FormValues>, form: FormApi) => {
    const {currentUser} = this.props;
    const {enterCredentialsVisible, resetPasswordVisible} = this.state;

    this.setState({
      submitting: true
    });

    if (enterCredentialsVisible && !currentUser) {
      this.login(values, form);
    } else if (resetPasswordVisible) {
      this.resetPassword(values);
    } else {
      this.createResetPasswordToken(values.email!);
    }
  };

  render(): React.ReactNode {

    const {isAuthenticated} = this.props;

    return (
        <StyledSegment placeholder>
          {isAuthenticated && <Redirect to="/"/>}
          <FinalForm
              onSubmit={this.handleSubmit}
              render={this.renderForm}
              subscription={{pristine: true}}
          />
        </StyledSegment>
    );
  }

  renderForm = ({form, handleSubmit}: FormRenderProps): React.ReactNode => {

    const {enterCredentialsVisible, chooseMandateVisible, forgotPasswordVisible} = this.state;
    const {validatingTokenVisible, resetPasswordVisible, error, success, errorMessage, submitting} = this.state;

    const transitionToForgotPassword = () =>
        this.startTransitioningTo("enterCredentialsVisible", "forgotPasswordVisible", form);
    const transitionToCredentials = () =>
        this.startTransitioningTo("forgotPasswordVisible", "enterCredentialsVisible", form);

    const transitionToRegistration = () => {
      const {history} = this.props;
      history.push("/registration");
    };

    return (
        <StyledForm onSubmit={handleSubmit}>
          <StyledGrid columns={2} stackable>
            <StyledRow verticalAlign="middle" textAlign="center">
              <Grid.Column verticalAlign="middle">
                <CredentialsSegment
                    error={error}
                    errorMessage={errorMessage}
                    submitting={submitting}
                    visible={enterCredentialsVisible}
                    transitionToCredentials={this.removeVerificationCodeInput}
                    transitionToForgotPassword={transitionToForgotPassword}
                    transitionToRegistration={transitionToRegistration}
                    renderMessage={this.renderMessage}
                    verificationCodeKey={this.state.verificationCodeKey}
                    verificationCodeInput={this.state.verificationCodeInput}
                    verificationCodeError={this.state.verificationCodeError}
                />
                <MandatesSegment visible={chooseMandateVisible}/>
                <ForgotPasswordSegment
                    success={success}
                    error={error}
                    errorMessage={errorMessage}
                    submitting={submitting}
                    visible={forgotPasswordVisible}
                    form={form}
                    transitionToCredentials={transitionToCredentials}
                    renderMessage={this.renderMessage}
                />
                <ValidatingTokenSegment
                    error={error}
                    visible={validatingTokenVisible}
                    errorMessage={errorMessage}
                    goToLogin={this.goToLogin}
                    renderMessage={this.renderMessage}
                />
                <ResetPasswordSegment
                    success={success}
                    error={error}
                    errorMessage={errorMessage}
                    submitting={submitting}
                    visible={resetPasswordVisible}
                    validatorImpl={this.validatorImpl}
                    goToLogin={this.goToLogin}
                    renderMessage={this.renderMessage}
                />
              </Grid.Column>
              <Gradient textAlign="center" verticalAlign="middle">
                <StyledHeader>Praxmedic</StyledHeader>
              </Gradient>
            </StyledRow>
          </StyledGrid>
        </StyledForm>
    );
  };

  renderMessage = (header: string, text: string, messageProps: MessageProps): React.ReactNode => {
    return (
        <Message {...messageProps}>
          <Message.Header>{header}</Message.Header>
          <p>{text}</p>
        </Message>
    )
  };
}

export default withRouterWorkaround(withAuthContext(withMessages(Login)));
